/**
 * THERE IS NO SIGN-IN SCREEN HERE (ruled 2026-08-25).
 *
 * A record's subdomain never asks who somebody is. They answered that
 * at the front door -- typed their work email, which is what chose this
 * subdomain -- so arriving either signs them in or sends them back.
 * Failing to get in means not getting past polywog.io.
 *
 * So this renders no button, no error, and no way to retry: a failed
 * trip returns to the apex, where the question belongs. That also
 * disposes of the redirect loop this path invites, without a guard --
 * a failure LEAVES rather than arriving again.
 *
 * THE ONE THING THAT STAYS is the two-factor challenge, and it is not
 * a sign-in screen. The person has already been identified; this is
 * them proving it is them, against a challenge bound to the attempt in
 * flight. It cannot happen anywhere else -- bouncing to the apex would
 * abandon the very attempt it belongs to.
 *
 * A DEVELOPER'S MACHINE IS OUTSIDE THIS LAW, because it is outside the
 * fleet: the host law says localhost is neither apex nor record, so
 * both of this screen's exits -- start the trip with the address the
 * front door sent, or return to the front door -- name a place that
 * does not exist locally. There is no address (nothing dispatched
 * here) and no apex (apexUrl of a one-label host is nonsense). So on a
 * local host, and only there, the screen shows a visible trigger: the
 * trip starts by hand and lands in the auth emulator's own account
 * chooser, which plays the part Google plays in the fleet. A refusal
 * stays on screen for the same reason -- there is nowhere to send it.
 */
import { useEffect, useState } from 'react';
import { ActionButton, DoorCard, Field, Notice, Stack, Text } from '../../../strata';
import {
  completeMfaSignIn,
  completeRedirectSignIn,
  mfaChallengeFromError,
  signInWithGoogle,
  type MfaChallenge,
} from '../session';
import { apexReturnUrl, apexUrl, invitedEmail, isLocalHost } from '../host';
import { Brand } from './Brand';

export function SignIn() {
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const local = isLocalHost(window.location.hostname);

  // A failure is either the 2FA challenge -- which belongs HERE, bound
  // to the attempt in flight -- or a real refusal, which belongs at the
  // front door. Nothing is shown for the second kind: the person goes
  // back and the apex owns the conversation. Except on a dev machine,
  // where there is no front door to own it -- the developer reads it
  // here or nowhere.
  const surface = (err: unknown) => {
    const mfa = mfaChallengeFromError(err);
    if (mfa) {
      setChallenge(mfa);
      return;
    }
    if (local) {
      setRefusal(String(err));
      return;
    }
    window.location.assign(apexUrl(window.location));
  };

  // NOBODY SHOULD HAVE TO PRESS ANYTHING HERE. They already said who
  // they are at the front door -- typed their work email, which is what
  // chose this subdomain. Asking again is the same question twice.
  //
  // So arriving signed-out STARTS the round trip. Google already holds
  // the consent (one client for the fleet), so for anyone who has been
  // here before it is a bounce they never see; a first-timer gets
  // Google's account chooser, which is the one screen that genuinely
  // needs them.
  //
  // A RECORD SIGNS IN THE PERSON THE APEX IDENTIFIED, and nobody else.
  // The address the front door sent is the whole warrant for being
  // here: it named this org, and it is what Google is told to sign in.
  //
  // WITHOUT IT, THEY DID NOT COME THROUGH THE FRONT DOOR -- they typed
  // the subdomain, followed a stale link, or came back on a trip that
  // carried nothing. All three want the same answer, and it is not a
  // Google account chooser: it is polywog.io, where the question of who
  // they are gets asked.
  //
  // That also disposes of the back button. Pressing back at Google
  // returns here without the fragment, which reads as "did not come
  // through the front door" rather than as a fresh arrival -- so the
  // trip is not started again and nobody is trapped between two pages.
  useEffect(() => {
    void completeRedirectSignIn().then((err) => {
      if (err) {
        surface(err);
        return;
      }
      // A dev machine dispatched nobody: no address arrives, and none
      // is missing. The trip waits for the trigger.
      if (local) return;
      const email = invitedEmail(window.location);
      if (email === null) {
        // The add-in's trip (PRD-553) rides back out to the apex and
        // returns with the address; any other arrival just leaves.
        window.location.assign(apexReturnUrl(window.location));
        return;
      }
      void run(() => signInWithGoogle(email));
    });
  }, []);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      surface(err);
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = (event: React.FormEvent) => {
    event.preventDefault();
    if (!challenge) return;
    void run(() => completeMfaSignIn(challenge, mfaCode.trim()));
  };

  return (
    <DoorCard>
      <Stack direction="column" gap="xl">
        <Brand />
        <Stack direction="column" gap="md">
          {challenge ? (
            <form onSubmit={submitMfa}>
              <Stack direction="column" gap="md">
                <Text tone="secondary">Enter the 6-digit code from your authenticator app.</Text>
                <Field
                  label="Authentication code"
                  labelHidden
                  inputMode="numeric"
                  pattern="[0-9]*"
                  isRequired
                  autoFocus
                  mono
                  value={mfaCode}
                  onChange={setMfaCode}
                  placeholder="123456"
                />
                <ActionButton variant="brand" type="submit" isPending={busy}>
                  Verify
                </ActionButton>
              </Stack>
            </form>
          ) : local ? (
            // The one visible trigger, dev machines only: no front
            // door dispatched anybody here, so the trip starts by
            // hand and lands in the emulator's account chooser.
            <>
              <Text tone="secondary">Local stack: sign in with the auth emulator.</Text>
              <ActionButton variant="brand" onPress={() => void run(() => signInWithGoogle(''))} isPending={busy}>
                Continue with Google
              </ActionButton>
              {refusal !== null && <Notice kind="error">{refusal}</Notice>}
            </>
          ) : (
            // The trip, in flight. Not a screen -- a transition. There
            // is nothing to decide and nothing to press.
            <Text tone="secondary">Signing you in\u2026</Text>
          )}
        </Stack>
      </Stack>
    </DoorCard>
  );
}
